import React, { useEffect, useState } from "react";
import { Users, Mail, Download } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");

export const AttendeesDialog = ({ event, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (event) api.get(`/admin/events/${event.id}/attendees`).then((r) => setData(r.data)).catch(() => setData({ attendees: [] }));
  }, [event]);

  const exportCsv = () => {
    const rows = [["Name", "Email", "Tickets", "Status", "Amount", "Booked"], ...data.attendees.map((a) => [a.name, a.email, a.quantity, a.status, a.amount, a.booked_at])];
    const blob = new Blob([rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${event.slug}-attendees.csv`;
    a.click();
  };

  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="attendees-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl inline-flex items-center gap-2"><Users size={20} /> Who's coming — {event?.title}</DialogTitle></DialogHeader>
        {!data ? <div className="py-8 text-center text-sm text-[color:var(--ink-2)]">Loading…</div> : (
          <>
            <div className="grid grid-cols-3 gap-3">
              {[["paid_tickets", "Tickets paid"], ["pending_tickets", "Pending"], ["revenue", "Revenue (AUD)"]].map(([k, l]) => (
                <div key={k} className="bg-[color:var(--sand)]/50 rounded-xl p-3" data-testid={`attendees-${k}`}>
                  <div className="font-display text-2xl text-[color:var(--ocean)]">{k === "revenue" ? `$${(data[k] || 0).toFixed(2)}` : data[k] || 0}</div>
                  <div className="text-[0.65rem] uppercase tracking-wider font-bold text-[color:var(--ink-2)]">{l}</div>
                </div>
              ))}
            </div>
            {data.event?.capacity > 0 && (
              <div className="text-xs text-[color:var(--ink-2)]">{data.paid_tickets} of {data.event.capacity} seats filled</div>
            )}
            {data.attendees.length === 0 ? (
              <div className="py-8 text-center text-sm text-[color:var(--ink-2)]" data-testid="attendees-empty">No bookings yet.</div>
            ) : (
              <>
                <div className="flex justify-end"><button onClick={exportCsv} data-testid="attendees-export" className="btn-pill btn-outline text-xs py-1.5"><Download size={13} /> Export CSV</button></div>
                <div className="rounded-xl border border-[color:var(--line)] overflow-hidden">
                  <table className="w-full text-sm" data-testid="attendees-table">
                    <thead className="text-left text-[0.65rem] uppercase tracking-wider text-[color:var(--ink-2)] bg-[color:var(--sand)]/40">
                      <tr><th className="px-3 py-2">Guest</th><th className="px-3 py-2">Tickets</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Booked</th></tr>
                    </thead>
                    <tbody>
                      {data.attendees.map((a, i) => (
                        <tr key={i} className="border-t border-[color:var(--line)]">
                          <td className="px-3 py-2">
                            <div className="font-semibold text-[color:var(--ocean)]">{a.name}</div>
                            {a.email && <a href={`mailto:${a.email}`} className="text-xs text-[color:var(--ink-2)] inline-flex items-center gap-1"><Mail size={10} /> {a.email}</a>}
                          </td>
                          <td className="px-3 py-2">{a.quantity}</td>
                          <td className="px-3 py-2"><span className={`chip ${a.status === "paid" ? "!bg-emerald-100 !text-emerald-800 !border-emerald-200" : "!bg-amber-100 !text-amber-800 !border-amber-200"}`}>{a.status}</span></td>
                          <td className="px-3 py-2 text-xs text-[color:var(--ink-2)]">{fmt(a.booked_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
